<script setup lang='ts'>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Language } from '@/types/locale.js';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore.js';
import { formatActivityDuration, formatSpokenLanguages } from '@/utils/utils.js';

import ViewMoreContent from './ViewMoreContent.vue';

const props = defineProps<{
  descriptions: any;
  nextSession: any;
  supplierName: string;
  spokenLanguages: any;
  documentations: Array<{ lang: Language; name: string; url: string }>;
}>();
const { t } = useI18n();
const globalConfiguration = useGlobalConfigurationStore();
const { currentLocale } = storeToRefs(globalConfiguration);

const extra = ['DETAIL', 'INCLUDED', 'NOT_INCLUDED', 'REQUIREMENTS', 'ESSENTIALS', 'GROUP'];
const showableContent = computed(() => !!props.descriptions?.some((it: any) => extra.includes(it.type)));
const detailContent = computed(() => props.descriptions?.find((it: any) => it.type === 'DETAIL')?.content);
const includedContent = computed(() => props.descriptions?.find((it: any) => it.type === 'INCLUDED')?.content);
const notIncludedContent = computed(() => props.descriptions?.find((it: any) => it.type === 'NOT_INCLUDED')?.content);
const requirementsContent = computed(() => props.descriptions?.find((it: any) => it.type === 'REQUIREMENTS')?.content);
const groupContent = computed(() => props.descriptions?.find((it: any) => it.type === 'GROUP')?.content);
const additionalContent = computed(() => props.descriptions?.find((it: any) => it.type === 'OTHER')?.content);

const essentialsContents = computed(() => props.descriptions?.filter((it: any) => it.type === 'ESSENTIALS'));
const hasEssentialsContents = computed(() => essentialsContents.value?.length > 0);

const hasPDFs = computed(() => props.documentations.length > 0);
const localizedDocumentation = computed(() => {
  return props.documentations.find(it => it.lang === currentLocale.value) ?? props.documentations.find(it => it.lang === null);
});

function openDocumentation() {
  if (localizedDocumentation.value?.url) {
    window.open(localizedDocumentation.value.url, '_blank', 'noopener,noreferrer');
  }
}

const spokenLanguages = computed(() => props.spokenLanguages);

const includedLabel = computed(() => {
  if (!includedContent.value && notIncludedContent.value)
    return t('content.not_included');
  return t('content.included');
});

const computedDuration = computed(() => {
  const numberOfDays = props.nextSession?.numberOfDays ?? 0;
  if (numberOfDays > 1) {
    return `${numberOfDays} jours`;
  }
  else if (props.nextSession?.duration) {
    return formatActivityDuration(props.nextSession.duration);
  }
  else {
    return null;
  }
});
</script>

<template>
  <section
    class="normal_spacing"
    :style="[showableContent || nextSession || spokenLanguages || supplierName ? 'display:block;' : 'display:none;']"
    aria-label="activité détaillée"
  >
    <h2 class="h2_title title_desktop">
      {{ t('content.experience') }}
    </h2>
    <div v-if="computedDuration" id="duration_wrapper" class="xs_spacing">
      <div aria-hidden="true">
        <svg
          width="24" height="24" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg"
          style="vertical-align:-3px;"
        >
          <g clip-path="url(#clip0_319_2120)">
            <path
              d="M12.9999 7.58328V12.9999L9.38883 14.8055M22.0277 12.9999C22.0277 17.9859 17.9859 22.0277 12.9999 22.0277C8.01404 22.0277 3.97217 17.9859 3.97217 12.9999C3.97217 8.01404 8.01404 3.97217 12.9999 3.97217C17.9859 3.97217 22.0277 8.01404 22.0277 12.9999Z"
              stroke="#252525" stroke-width="2.16667" stroke-linecap="round" stroke-linejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_319_2120">
              <rect width="21.6667" height="21.6667" fill="white" transform="translate(2.1665 2.16663)" />
            </clipPath>
          </defs>
        </svg>
      </div>
      <p class="text_content">
        <span>{{ t('menu.duration') }} :</span> {{ computedDuration }}
      </p>
    </div>
    <div v-if="spokenLanguages" id="languages_wrapper" class="xs_spacing">
      <div aria-hidden="true">
        <svg width="24" height="24" viewBox="0 0 24 24" style="vertical-align: -3px;">
          <path
            d="M8.25 3a.75.75 0 100 1.5h10c1.24 0 2.25 1 2.25 2.25V17a.75.75 0 001.5 0V6.75A3.75 3.75 0 0018.25 3h-10zM7.25 16.25a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5zM12.25 15a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0zM16 15a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z"
          />
          <path
            fill-rule="evenodd" clip-rule="evenodd"
            d="M3 8.13c0-1.18.97-2.16 2.15-2.16L11 6l5.85.02c1.18 0 2.16.98 2.16 2.16v9.68c0 1.18-.97 2.15-2.15 2.15h-8.8L2.54 21.4a.75.75 0 01-.9-.97L3 16.32V8.13zm1.5 0v8.3c0 .09-.01.17-.04.25L3.5 19.6l4.3-1.07a.75.75 0 01.19-.03h8.88c.35 0 .66-.3.66-.65v-4.69l-.01-4.99c0-.35-.3-.66-.66-.66-.38 0-3.11 0-5.84-.02H11l-5.85-.02c-.35 0-.65.3-.65.66z"
          />
        </svg>
      </div>
      <p class="text_content">
        <span>{{ t('content.spoken_languages') }} :</span> {{ formatSpokenLanguages(spokenLanguages) }}
      </p>
    </div>
    <p v-if="supplierName" class="supplier">
      {{ t('content.powered_by') }} {{ supplierName }}
    </p>
    <section :style="[detailContent ? 'display:block;' : 'display:none;']" aria-label="details">
      <div class="normal_spacing">
        <ViewMoreContent :html-content="detailContent" />
      </div>
    </section>
    <section v-if="hasPDFs" class="normal_spacing">
      <h4>Documentation</h4>
      <button class="documentation_button" :disabled="!localizedDocumentation?.url" @click="openDocumentation">
        <span aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-icon lucide-eye"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></svg>
        </span>
        {{ localizedDocumentation?.name ?? 'Informations complémentaires' }}
      </button>
    </section>
    <hr v-if="hasPDFs">
    <section
      class="extra_content_grid"
      :style="[includedContent || notIncludedContent ? 'display:grid;' : 'display:none;']"
    >
      <h3 class="content_title">
        {{ includedLabel }}
      </h3>
      <div>
        <div v-if="includedContent" class="content_wrapper">
          <div style="margin-right:0.5rem;">
            <svg
              width="18" height="18" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"
              style="vertical-align:-3px;"
            >
              <path
                d="M1 5.50004L5.49996 10L14.499 1" stroke="#007850" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <p v-html="includedContent" />
        </div>
        <div v-if="notIncludedContent" class="content_wrapper">
          <div style="margin-right:0.5rem;">
            <svg
              width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg"
              style="vertical-align:-3px;"
            >
              <path
                d="M11 11.5L6 6.5M6 6.5L1 1.5M6 6.5L11 1.5M6 6.5L1 11.5" stroke="#C72A3D" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
          </div>
          <p v-html="notIncludedContent" />
        </div>
      </div>
    </section>
    <hr v-if="includedContent || notIncludedContent">
    <div
      :style="[requirementsContent || hasEssentialsContents || groupContent || additionalContent ? '' : 'display:none;']"
    >
      <section class="extra_content_grid" :style="[requirementsContent ? '' : 'display:none;']">
        <h3 class="content_title">
          {{ t('content.conditions') }}
        </h3>
        <p class="text_content" v-html="requirementsContent" />
      </section>
      <hr v-if="requirementsContent && (hasEssentialsContents || groupContent)">
      <section class="extra_content_grid" :style="[hasEssentialsContents ? '' : 'display:none;']">
        <h3 class="content_title">
          {{ t('content.essentials') }}
        </h3>
        <div>
          <p
            v-for="(essential, index) in essentialsContents" :key="index" class="text_content"
            v-html="essential.content"
          />
        </div>
      </section>
      <hr v-if="hasEssentialsContents && groupContent">
      <section class="extra_content_grid" :style="[groupContent ? '' : 'display:none;']">
        <h3 class="content_title">
          {{ t('content.groups') }}
        </h3>
        <p class="text_content" v-html="groupContent" />
      </section>
      <hr v-if="(hasEssentialsContents || groupContent) && additionalContent">
      <section class="extra_content_grid" :style="[additionalContent ? '' : 'display:none;']">
        <h3 class="content_title">
          {{ t('content.other') }}
        </h3>
        <p class="text_content" v-html="additionalContent" />
      </section>
      <hr>
    </div>
  </section>
</template>

<style scoped>
.h2_title.title_desktop {
  @media screen and (width >= 1024px) {
    margin-bottom: 0.75rem;
  }
}

.content_title {
  font-size: 18px;
  font-weight: var(--yb-text-bold);
  margin-bottom: var(--yb-spacing-sm);

  @media screen and (width >= 1024px) {
    margin-bottom: none;
  }
}

.text_content {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-light);
  line-height: var(--yb-text-lineheight);
}

.xs_spacing {
  margin-bottom: 0.75rem;
}

.small_spacing {
  margin-bottom: var(--yb-spacing-sm);
}

.normal_spacing {
  margin-bottom: var(--yb-radius-base);
}

.supplier {
  padding-bottom: var(--yb-spacing-sm);
  font-size: 16px;
  font-weight: var(--yb-text-bold);
}

hr {
  border: 0;
  height: 1px;
  background: #333;
  background-image: linear-gradient(to right, #ccc, #333, #ccc);
}

.extra_content_grid {
  display: grid;
  grid-template-columns: 1fr;
  padding-block: var(--yb-spacing-sm);

  .content_wrapper {
    display: grid;
    grid-template-columns: 20px 1fr;
    gap: var(--yb-radius-sm);
  }

  @media screen and (width >= 1024px) {
    grid-template-columns: 200px 1fr;
  }
}

.documentation_button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  border: 2px solid black;
  cursor: pointer;
  margin-top: 0.5rem;
  text-transform: uppercase;

  &:hover {
    background-color: var(--yb-color-lightgray);
    color: var(--yb-color-black);
  }

  span {
    position: relative;
    top: 2px;
  }
}

#duration_wrapper,
#languages_wrapper {
  display: flex;
  align-items: center;
  gap: var(--yb-radius-sm);
}
</style>
